import { Stack, Typography, Box, alpha, Skeleton, CircularProgress, Tooltip, Theme, tooltipClasses, styled, TooltipProps } from "@mui/material";
import { useRegionsQuery } from "src/context/api/non-branded/api";
import { useGetBQCountriesQuery } from "src/context/api/ext/api";
import { useCurrentWorkspace } from "src/context/reducers/app-settings";
import { useSelector } from "react-redux";
import { RootState } from "src/context/store";
import { fDate } from "src/utils/format-time";
import { useState, useMemo, useEffect, useRef } from "react";
import Iconify from "src/components/iconify";
import { useNonBrandedContext } from "./context/use-non-branded";
import useLocales from "src/locales/use-locales";



const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: theme.palette.common.white,
      color: 'rgba(0, 0, 0, 0.87)',
      boxShadow: theme.shadows[1],
      fontSize: 11,
    },
  }));

// Map region names to country codes
const getCountryCodeFromRegionName = (regionName: string, countries: any[]): string | null => {
    if (!countries || countries.length === 0) return null;

    // Try exact match first
    const exactMatch = countries.find(
        (c) => c.country_name?.toLowerCase() === regionName?.toLowerCase()
    );
    if (exactMatch) return exactMatch.country_code;

    // Try partial match (e.g., "France" matches "France")
    const partialMatch = countries.find(
        (c) => regionName?.toLowerCase().includes(c.country_name?.toLowerCase()) ||
            c.country_name?.toLowerCase().includes(regionName?.toLowerCase())
    );
    if (partialMatch) return partialMatch.country_code;

    // Fallback: try to extract code from region name if it looks like a code
    if (regionName && regionName.length === 2) {
        return regionName.toUpperCase();
    }

    return null;
};

// Component to handle icon loading with loader
function IconifyWithLoader({ icon, width, height }: { icon: string; width: number; height: number }) {
    const [isLoaded, setIsLoaded] = useState(false);
    const iconRef = useRef<HTMLSpanElement>(null);

    useEffect(() => {
        // Reset loading state when icon changes
        setIsLoaded(false);

        // Check if icon is already loaded
        const checkIconLoaded = () => {
            if (iconRef.current) {
                const svg = iconRef.current.querySelector('svg');
                if (svg) {
                    setIsLoaded(true);
                }
            }
        };

        // Use MutationObserver to detect when the icon SVG is injected into the DOM
        if (iconRef.current) {
            const observer = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                    if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
                        checkIconLoaded();
                    }
                });
            });

            observer.observe(iconRef.current, {
                childList: true,
                subtree: true,
            });

            // Also check immediately in case it's already loaded
            checkIconLoaded();

            // Fallback timeout to show icon after 2 seconds even if not detected
            const timeout = setTimeout(() => {
                setIsLoaded(true);
            }, 2000);

            return () => {
                observer.disconnect();
                clearTimeout(timeout);
            };
        }
    }, [icon]);

    return (
        <Box
            sx={{
                position: 'relative',
                display: 'inline-flex',
                width: width,
                height: height,
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            {!isLoaded && (
                <CircularProgress
                    size={10}
                    thickness={5}
                    sx={{
                        color: '#919EAB',
                    }}
                />
            )}
            <Box
                ref={iconRef}
                sx={{
                    display: isLoaded ? 'inline-flex' : 'none',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Iconify icon={icon} width={width} height={height} />
            </Box>
        </Box>
    );
}

export default function RegionSelector() {
    const workspace = useCurrentWorkspace();
    const filterStore = useSelector((state: RootState) => state.filters);
    const { selectedRegion, setSelectedRegion } = useNonBrandedContext();
    const { t } = useLocales();

    const { data: regionsData, isLoading, isFetching, isUninitialized } = useRegionsQuery({
        workspace_id: workspace?.id as number,
        start_date: fDate(filterStore.start, "yyyy-MM-dd"),
        end_date: fDate(filterStore.end, "yyyy-MM-dd"),
        devices: filterStore.devices,
        countries: selectedRegion ? [selectedRegion] : [],
        platforms: filterStore.platforms,
        search_terms: filterStore.keywords,
    }, { skip: !workspace?.id });

    const { data: countries } = useGetBQCountriesQuery();

    const regions = regionsData?.data || [];


    useEffect(() => {
        if (regionsData && selectedRegion === null) {
            setSelectedRegion([...regionsData.data].sort((a, b) => b.perc_clicks - a.perc_clicks)[0]?.region_name);
        }
    }, [regionsData]);

    // Map regions with country codes
    const regionsWithCodes = useMemo(() => {
        return regions.map((region) => {
            const countryCode = getCountryCodeFromRegionName(region.region_name, countries || []);
            return {
                ...region,
                countryCode: countryCode || region.region_name.slice(0, 2),
            };
        });
    }, [regions, countries]);

    // Set first region as selected by default
    useEffect(() => {
        if (regionsWithCodes.length > 0 && !selectedRegion) {
            setSelectedRegion(regionsWithCodes[0].region_name);
        }
    }, [regionsWithCodes, selectedRegion]);

    if (regionsWithCodes.length === 0) {
        return null;
    }

    if (isUninitialized || isLoading || isFetching) {
        return <Box px={"24px"}>
            <Skeleton sx={{ borderRadius: 1 }} variant="rectangular" width="100%" height={40} />
        </Box>;
    }

    return (
        <Stack
            direction="row"
            alignItems="flex-start"
            spacing={"16px"}
            sx={{
                mx: 0,
                py: 0,
                px: '24px',
                pb: 0,
                mb: 1.5,
                borderBottom: "1px solid rgba(145, 158, 171, 0.2)",
                overflowX: "auto",
                "&::-webkit-scrollbar": {
                    display: "none",
                },
                scrollbarWidth: "none",
            }}

        >
            {regionsWithCodes.map((region) => {
                const isSelected = selectedRegion === region.region_name;

                return (
                    <Box
                        component="button"
                        key={region.region_name}
                        onClick={() => {
                            setSelectedRegion(region.region_name);
                        }}
                        sx={{
                            appearance: "none",
                            background: "none",
                            border: "none",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                            px: 0,
                            py: 0,
                            pb: 1,
                            cursor: "pointer",
                            borderBottom: isSelected ? "2px solid black" : "2px solid transparent",
                            transition: "border-color 0.2s ease",
                            "&:hover": {
                                opacity: 0.8,
                            },
                        }}
                    >
                        <IconifyWithLoader icon={`circle-flags:${region.countryCode}`} width={16} height={16} />
                        <Stack direction="row" px={"6px"} spacing={0.5} alignItems="center">
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 400,
                                    fontSize: "0.875rem",
                                    color: isSelected ? "black" : "#919EAB",
                                }}
                            >
                                {region.region_name}
                            </Typography>
                            <LightTooltip sx={{
                                [`& .${tooltipClasses.tooltip}`]: (theme: Theme) => ({
                                    backgroundColor: theme.palette.common.white,
                                    color: 'rgba(0, 0, 0, 0.87)',
                                    boxShadow: theme.shadows[1],
                                    fontSize: 11,
                                }),
                            }} title={t("dashboard.nonBrand.tooltips.shareOfClicks")}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 400,
                                        fontSize: "0.875rem",
                                        color: "#919EAB",
                                    }}
                                >
                                    ({Math.round(region.perc_clicks)}%)
                                </Typography>
                            </LightTooltip>
                        </Stack>
                    </Box>
                );
            })}
        </Stack>
    );
}
